/**
 * Markdown Node Renderer
 *
 * Renders markdown-type nodes with full markdown content (via `marked`).
 * Supports LOD for texture caching at distance, but always renders full
 * markdown when in HTML mode.
 *
 * Phase 0.7: Uses WebGL texture caching for distant nodes.
 */

import { marked } from 'marked';
import type { UINode, ScreenMetrics, WorldRect } from '../types';
import type { NodeRenderer, RenderLayers, RenderResult, LODLevel } from '../renderer';
import type { MeasureOptions } from '../content-measure';
import { hashContent } from '../texture-cache';

// Configure marked for synchronous rendering with GFM
marked.use({ async: false, gfm: true, breaks: true });

/** Default LOD threshold below which we use textures (in screen pixels) */
const DEFAULT_TEXTURE_THRESHOLD = 60;

/** Default screen size threshold for texture rendering when forceTextureAtDistance is enabled */
const DEFAULT_FORCE_TEXTURE_THRESHOLD = 150;

/**
 * Markdown renderer with content-aware LOD and texture caching.
 */
export class MarkdownRenderer implements NodeRenderer {
	private lastLOD: Map<string, LODLevel> = new Map();
	private lastContent: Map<string, string> = new Map();
	private contentHashes: Map<string, string> = new Map();

	render(node: UINode, metrics: ScreenMetrics, layers: RenderLayers): RenderResult {
		const screenSize = Math.max(metrics.width, metrics.height);
		const opts = layers.options;

		// Use forceLod if set, otherwise always 'full' for markdown
		const lod: LODLevel = opts.forceLod ?? 'full';

		// Calculate content hash for texture cache invalidation
		const baseContentHash = this.getContentHash(node);
		const contentHash = `${baseContentHash}:${lod}`;
		const textureId = `${node.id}:${lod}`;

		// Determine if we should use texture rendering
		// forceTextureRendering forces texture mode regardless of screen size
		const textureThreshold = opts.forceTextureAtDistance
			? (opts.lodFullThreshold ?? DEFAULT_FORCE_TEXTURE_THRESHOLD)
			: (opts.textureThreshold ?? DEFAULT_TEXTURE_THRESHOLD);
		const wantTexture = (opts.forceTextureRendering || screenSize < textureThreshold) && layers.webgl !== null;

		const hasValidTexture = wantTexture && layers.webgl?.hasValidTexture(textureId, contentHash);

		const lastLod = this.lastLOD.get(node.id);
		const lodChanged = lastLod !== lod;
		const isNewElement = layers.html.isNewlyCreated(node.id);

		layers.html.setDataAttribute(node.id, 'lod', lod);

		// Try texture rendering if we have a valid texture
		if (hasValidTexture && layers.webgl) {
			const worldRect: WorldRect = {
				x: node.position.x,
				y: node.position.y,
				width: node.size.width,
				height: node.size.height
			};

			// Rotation is applied via shader (texture is captured without rotation for efficiency)
			const textureDrawn = layers.webgl.drawCachedTexture(textureId, worldRect, 1, node.rotation ?? 0);

			if (textureDrawn) {
				layers.html.setContent(node.id, '');
				layers.html.addClass(node.id, 'texture-cached');
				this.lastContent.delete(node.id);
				this.lastLOD.set(node.id, lod);
				return { lod, changed: lodChanged };
			}
		}

		// HTML rendering
		layers.html.removeClass(node.id, 'texture-cached');

		const content = this.renderContent(node);
		const lastContentValue = this.lastContent.get(node.id);
		const contentChanged = lastContentValue !== content || isNewElement;

		if (contentChanged) {
			layers.html.setContent(node.id, content);
			this.lastContent.set(node.id, content);
		}

		// Request texture generation for distant rendering
		if (wantTexture && layers.webgl && !hasValidTexture) {
			layers.webgl.requestTexture(
				textureId,
				() => layers.html.getContainer(node.id),
				contentHash,
				1,
				lod,
				textureThreshold
			);
		}

		this.lastLOD.set(node.id, lod);

		return { lod, changed: lodChanged || contentChanged };
	}

	private hasWordWrap(node: UINode): boolean {
		const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
		if (typeof obj.wordWrap === 'boolean') return obj.wordWrap;
		return true; // markdown default
	}

	private renderContent(node: UINode): string {
		const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
		const content = this.getContent(obj);
		const isEmpty = !content || content.trim().length === 0;
		const wordWrap = this.hasWordWrap(node);
		const wrapStyle = wordWrap ? '' : ' style="white-space: nowrap; overflow: visible"';

		const bodyHtml = isEmpty ? '' : marked.parse(content) as string;

		return `
			<div class="note-node note-full">
				<div class="node-close-btn" data-action="close">&times;</div>
				<div class="note-editor"${wrapStyle} data-empty="${isEmpty}">${bodyHtml}</div>
			</div>
		`;
	}

	private getContent(obj: Record<string, unknown>): string {
		return typeof obj?.content === 'string' ? (obj.content as string) : '';
	}

	/**
	 * Get a content hash for cache invalidation.
	 */
	private getContentHash(node: UINode): string {
		const cached = this.contentHashes.get(node.id);
		const contentStr = JSON.stringify(node.data);

		if (cached) {
			return cached;
		}

		const hash = hashContent(contentStr);
		this.contentHashes.set(node.id, hash);
		return hash;
	}

	private escapeHtml(text: string): string {
		return text
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	getContentHTML(node: UINode): string {
		// Use renderContent which already respects wordWrap
		return this.renderContent(node);
	}

	getMeasureOptions(node: UINode): MeasureOptions {
		const obj = (node.data.obj as Record<string, unknown>) ?? node.data;
		const content = (obj?.content as string) ?? '';
		const hasImages = /!\[.*?\]\(.*?\)/.test(content);
		const hasTables = /\|.*\|.*\n\|[\s\-:|]*\|/.test(content);
		// Strip image markdown to get text-only length for prose-width heuristic
		const textLength = hasImages
			? content.replace(/!\[.*?\]\(.*?\)/g, '').trim().length
			: content.length;

		let minWidth = 200;
		if (hasImages && textLength === 0) minWidth = 100;
		// Tables need extra width for readable column layout
		if (hasTables) minWidth = Math.max(minWidth, 300);

		return {
			maxWidth: 640,
			minWidth,
			minHeight: 60,
			contentLength: textLength,
			preferPortrait: !hasImages && !hasTables,
		};
	}

	getPreferredSize(node: UINode): { width: number; height: number } {
		return {
			width: node.size?.width ?? 640,
			height: node.size?.height ?? 80
		};
	}

	cleanup(nodeId: string): void {
		this.lastLOD.delete(nodeId);
		this.lastContent.delete(nodeId);
		this.contentHashes.delete(nodeId);
	}
}
